import React from 'react';
import { observer } from 'mobx-react';
import { DataGrid, SearchToolbar, View, ViewHeader } from '../../../../../core/components/Index';
import { useNavigate } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { Column } from 'primereact/column';
import Helper, { formatDate } from '../../../../../core/services/Helper';
import SpcModule from '../../SpcModule';
import { Appl } from '../../../../../core/Appl';
import { DataTableSelectEvent } from 'primereact/datatable';
import { AppPermissions } from '../../../../../core/services/AppPermissions';
import { SpcSubmissionListModel } from './SpcSubmissionListModel';
import SpcSubmissionListViewModel from './SpcSubmissionListViewModel';
import { Button as PrimeButton } from 'primereact/button';
import { FileInput } from 'lucide-react';

export const SpcSubmissionListView: React.FC = observer(() => {
  const vm = Appl.Services.get<SpcSubmissionListViewModel>('SpcSubmissionListViewModel');
  vm.navigate = useNavigate();

  useAsyncEffect(async () => {
    await vm.initAsync();
    await vm.getPagedAsync();
  }, []);

  const requestIdTemplate = (row: SpcSubmissionListModel) => {
    return `SPC-${row.spcTypeSubId}`;
  };

  const createDateTemplate = (row: SpcSubmissionListModel) => {
    return formatDate(row.createDate);
  };

  const startDateTemplate = (row: SpcSubmissionListModel) => {
    return formatDate(row.startDate);
  };
  const endDateTemplate = (row: SpcSubmissionListModel) => {
    return formatDate(row.endDate);
  };

   const amountTemplate = (row: SpcSubmissionListModel) => {
    return Helper.formatCurrency(row.estmComp);
  };

  const onRowSelect = (event: DataTableSelectEvent) => {
    let editLink = `/spc/spc-queue/estimate-details/${event.data.spcTypeSubId}`;
    vm.navigate(editLink);
  };

  return (
    <>
      <View id="SpcSubmissionListView" pageTitle={vm.pageTitle}
        authKey={AppPermissions.AuthenticatedUser}
        showMenuBar={false}
        menuItems={SpcModule.MenuItems()}
      >
        <ViewHeader title={vm.pageTitle} description={vm.pageDescription}>
          <SearchToolbar
            filterPlaceholder="ID / Member / Email / Type of Purchase"
            filter={vm.searchRequest?.filter}
            onFilterChange={(e) => vm.onInputChange(e, 'filter', vm.searchRequest)}
            onRefresh={async () => await vm.getPagedAsync()}
            addNewVisible={false}
            refreshVisible={false}
            showButton={false}
            searchOnInputChange={true}
          />
        </ViewHeader>
         <div className="flex space-x-1">
          <PrimeButton outlined severity="secondary" onClick={async () => await vm.export('csv')}
            className="!border-transparent !bg-transparent">
            <FileInput className="w-4 h-4 mr-1.5" />
            Export
          </PrimeButton>
        </div>
        <DataGrid
          pageSize={vm.pageSize}
          dataSource={vm.items}
          totalRowsCount={vm.totalRowsCount}
          searchRequest={vm.searchRequest}
          onRefresh={() => vm.getPagedAsync()}
          selectionMode='single'
          onRowSelect={onRowSelect}
        >
          <Column field="spcTypeSubId" header="ID" sortable={true} hidden={false} body={requestIdTemplate} />
          <Column field="createDate" header="CREATED ON" sortable={true} body={createDateTemplate} />
          <Column field="memberName" header="MEMBER NAME" sortable={true} />
          <Column field="memberEmail" header="EMAIL ADDRESS" sortable={true} />
          <Column field="spcTypeName" header="TYPE OF PURCHASE" sortable={true} />
          <Column field="startDate" header="START DATE" sortable={true} body={startDateTemplate} />
          <Column field="endDate" header="END DATE" sortable={true} body={endDateTemplate} />
          <Column field="estmComp" header="AMOUNT" sortable={true} body={amountTemplate} />
        </DataGrid>
      </View>
    </>
  );
});
